import { NavLink, Outlet } from 'react-router-dom';
import { ROLE_META, type Permission, type Role } from '@accel/shared';
import { useAuth, useSession } from '../auth/AuthContext';

interface NavEntry {
  section?: string;
  to?: string;
  label?: string;
  /** Hidden unless the session holds this permission. */
  needs?: Permission;
}

/**
 * Navigation is derived from the session's role, but every entry is also
 * permission-gated. Hiding a link is presentation; the API refusing the call
 * is the actual control.
 */
const NAV: Record<Role, NavEntry[]> = {
  OPS: [
    { section: 'Control' },
    { to: '/', label: 'Operations Home' },
    { to: '/deals', label: 'All Deals', needs: 'deal:read' },
    { to: '/deals/new', label: 'New Deal', needs: 'deal:create' },
    { section: 'Decisioning' },
    { to: '/deals?status=SUBMITTED', label: 'Validation Queue', needs: 'deal:validate' },
    { to: '/deals?status=REFERRED', label: 'Manual Review', needs: 'deal:review' },
    { to: '/deals?status=CONFIRMED,PARTIALLY_CONFIRMED', label: 'Routing', needs: 'deal:route' },
    { section: 'Settlement' },
    { to: '/deals?status=OUTSTANDING,OVERDUE', label: 'Reconciliation', needs: 'deal:settle' },
    { section: 'Books' },
    { to: '/ledger', label: 'Ledger & Trial Balance', needs: 'ledger:read' },
    { section: 'Reference' },
    { to: '/parties', label: 'Counterparties', needs: 'party:read' },
    { to: '/programmes', label: 'Programmes', needs: 'programme:read' },
  ],
  PLATFORM_ADMIN: [
    { section: 'Control' },
    { to: '/', label: 'Home' },
    { to: '/deals', label: 'All Deals', needs: 'deal:read' },
    { section: 'Configuration' },
    { to: '/users', label: 'People & Access', needs: 'user:read' },
    { to: '/programmes', label: 'Programmes', needs: 'programme:read' },
    { to: '/parties', label: 'Counterparties', needs: 'party:read' },
    { section: 'Books' },
    { to: '/ledger', label: 'Ledger & Trial Balance', needs: 'ledger:read' },
  ],
  UNDERWRITER: [
    { section: 'Underwriting' },
    { to: '/', label: 'Home' },
    { to: '/deals?status=REFERRED', label: 'Manual Review', needs: 'deal:review' },
    { to: '/deals', label: 'All Deals', needs: 'deal:read' },
    { section: 'Reference' },
    { to: '/parties', label: 'Counterparties', needs: 'party:read' },
    { to: '/programmes', label: 'Programmes', needs: 'programme:read' },
  ],
  SME: [
    { section: 'Receivables' },
    { to: '/', label: 'Overview' },
    { to: '/deals/new', label: 'Submit a receivable', needs: 'deal:create' },
    { to: '/deals', label: 'My Deals', needs: 'deal:read' },
    { to: '/deals?status=ROUTED', label: 'Financing Offers', needs: 'deal:offer' },
    { section: 'Money' },
    { to: '/ledger', label: 'Statement of Account', needs: 'statement:read' },
  ],
  PAYER: [
    { section: 'Confirmation' },
    { to: '/', label: 'Overview' },
    { to: '/deals?status=AWAITING_CONFIRMATION', label: 'Confirmation Inbox', needs: 'deal:confirm' },
    { to: '/deals', label: 'All Invoices', needs: 'deal:read' },
    { section: 'Settlement' },
    { to: '/deals?status=OUTSTANDING,OVERDUE', label: 'Due for Settlement', needs: 'deal:read' },
  ],
  LENDER: [
    { section: 'Origination' },
    { to: '/', label: 'Overview' },
    { to: '/deals?status=ROUTED', label: 'Deal Inbox', needs: 'deal:read' },
    { section: 'Book' },
    { to: '/deals?status=FINANCED,OUTSTANDING,OVERDUE', label: 'Portfolio', needs: 'deal:read' },
    { to: '/ledger', label: 'Settlements', needs: 'ledger:read' },
  ],
  AUDITOR: [
    { section: 'Read-only' },
    { to: '/', label: 'Overview' },
    { to: '/deals', label: 'All Deals', needs: 'deal:read' },
    { to: '/ledger', label: 'Ledger & Trial Balance', needs: 'ledger:read' },
    { to: '/parties', label: 'Counterparties', needs: 'party:read' },
  ],
};

export function AppShell() {
  const session = useSession();
  const { logout, switchMembership, stopViewingAs, can } = useAuth();
  const role = session.membership.role;
  const entries = NAV[role] ?? [];
  const viewingAs = session.impersonatedBy;

  return (
    <div className="app">
      {/* Impossible to miss, and always the way out. Somebody who forgets they
          are in a borrowed session is how an action gets taken under the
          wrong name. */}
      {viewingAs ? (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '9px 18px',
            background: 'var(--amber)',
            color: '#fff',
            fontSize: 13.5,
            fontWeight: 600,
          }}
        >
          <span>
            You are viewing as <strong>{session.user.name}</strong> ({session.membership.roleName}
            {session.membership.partyName ? ` · ${session.membership.partyName}` : ''}). Anything you
            do is recorded against them, and against you.
          </span>
          <button
            className="btn btn-ghost"
            style={{ marginLeft: 'auto', background: '#fff' }}
            onClick={stopViewingAs}
          >
            Stop viewing as
          </button>
        </div>
      ) : null}

      <header className="topbar">
        <div className="brand">
          <div>
            <div className="brand-name">Accel</div>
            <div className="brand-tag">Embedded Finance</div>
          </div>
        </div>

        <div className="topbar-spacer" />

        {session.availableMemberships.length > 1 ? (
          <div>
            <span className="ctx-label">Acting as</span>
            <select
              className="select"
              value={session.membership.id}
              onChange={(e) => void switchMembership(e.target.value)}
            >
              {session.availableMemberships.map((m) => (
                <option key={m.id} value={m.id}>
                  {ROLE_META[m.role].name}
                  {m.partyName ? ` — ${m.partyName}` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div>
          <span className="ctx-label">Tenant</span>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>{session.membership.tenantName}</div>
        </div>

        <button className="btn btn-ghost" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      <aside className="sidebar">
        <div className="role-switch">
          <div className="lbl">Signed in as</div>
          <div className="role-card on">
            <div className="role-ico">
              {session.user.name
                .split(/\s+/)
                .slice(0, 2)
                .map((w) => w[0]?.toUpperCase() ?? '')
                .join('')}
            </div>
            <div className="role-meta">
              <div className="role-name">{session.user.name}</div>
              <div className="role-sub">
                {ROLE_META[role].name}
                {session.membership.partyName ? ` · ${session.membership.partyName}` : ''}
              </div>
            </div>
          </div>
        </div>

        <div className="nav-div" />

        <nav className="nav">
          {entries.map((entry, i) => {
            if (entry.section) {
              return (
                <div className="nav-sec" key={`s${i}`}>
                  {entry.section}
                </div>
              );
            }
            if (entry.needs && !can(entry.needs)) return null;
            return (
              <NavLink
                key={entry.to}
                to={entry.to!}
                // Exact matching throughout: NavLink prefix-matches by
                // default, so "/deals" would also light up on "/deals/new".
                end
                className={({ isActive }) =>
                  // A filtered link shares its path with the unfiltered list, so
                  // compare the query too or several entries light up at once.
                  `nav-item ${isActive && matchesQuery(entry.to!) ? 'on' : ''}`
                }
              >
                {entry.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="side-foot">
          <b>Accel</b> · Embedded Finance
          <br />
          Invoice discounting &amp; order finance
          <br />
          <span style={{ opacity: 0.7 }}>SnapHub Technologies</span>
        </div>
      </aside>

      <main className="main">
        <div className="main-inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function matchesQuery(to: string): boolean {
  const [, query = ''] = to.split('?');
  return window.location.search.replace(/^\?/, '') === query;
}
